// Web-compatible on record API Client
// Adapted from React Native OnRecordAPI.ts for web use

const API_CONFIG = {
  // Backend V3 Cloudflare Workers Architecture
  services: {
    auth: import.meta.env.VITE_AUTH_API_URL || 'https://auth.onrecord.live',
    user: import.meta.env.VITE_USER_API_URL || 'https://auth.onrecord.live',
    post: import.meta.env.VITE_POST_API_URL || 'https://api.onrecord.live',
  },
  // Legacy URL (fallback)
  baseURL: import.meta.env.VITE_API_URL || 'https://api.onrecord.live',
  timeout: 15000,
};

interface Post {
  postId: string;
  userId: string;
  type: 'text' | 'image' | 'video';
  content: string;
  mediaUri?: string;
  mediaType?: string;
  thumbnailUri?: string;
  location?: {
    city: string;
    country: string;
    coordinates: [number, number];
  };
  onRecordCode?: string;
  privacy: string;
  allowComments: boolean;
  createdAt: string;
  capturedAt?: string;
  updatedAt: string;
  author: {
    id: string;
    name: string;
    username: string;
    avatar?: string;
    verified: boolean;
    isReporter?: boolean;
  };
  stats: {
    likes: number;
    comments: number;
    reposts: number;
    views: number;
  };
}

class OnRecordWebAPI {
  private baseURL: string;
  private authURL: string;
  private userURL: string;
  private postURL: string;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.authURL = API_CONFIG.services.auth;
    this.userURL = API_CONFIG.services.user;
    this.postURL = API_CONFIG.services.post;
  }

  // ============= Authentication Methods =============

  async getAuthToken(): Promise<string | null> {
    return localStorage.getItem('auth_token');
  }

  async getRefreshToken(): Promise<string | null> {
    return localStorage.getItem('refresh_token');
  }

  async setAuthToken(token: string): Promise<void> {
    localStorage.setItem('auth_token', token);
    // Decode JWT to get expiry time and store it
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp) {
        localStorage.setItem('token_expiry', payload.exp.toString());
      }
    } catch (e) {
      console.warn('Failed to decode token expiry:', e);
    }
  }

  async setRefreshToken(refreshToken: string): Promise<void> {
    localStorage.setItem('refresh_token', refreshToken);
  }

  async setTokens(accessToken: string, refreshToken: string): Promise<void> {
    await this.setAuthToken(accessToken);
    await this.setRefreshToken(refreshToken);
  }

  async clearAuthToken(): Promise<void> {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('isLoggedIn');
  }

  async isTokenExpired(): Promise<boolean> {
    const expiryStr = localStorage.getItem('token_expiry');
    if (!expiryStr) return true;

    const expiry = parseInt(expiryStr);
    const now = Math.floor(Date.now() / 1000);

    // Consider token expired if it expires in the next 5 minutes
    return expiry - now < 300;
  }

  async refreshAccessToken(): Promise<boolean> {
    try {
      const refreshToken = await this.getRefreshToken();
      if (!refreshToken) {
        console.warn('No refresh token available');
        return false;
      }

      console.log('🔄 Refreshing access token...');

      const response = await fetch(`${this.authURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken,
        }),
      });

      if (!response.ok) {
        console.error('Token refresh failed:', response.status);
        // If refresh fails, clear all auth data
        await this.clearAuthToken();
        return false;
      }

      const data = await response.json();

      const newAccessToken = data.tokens?.accessToken || data.token;
      const newRefreshToken = data.tokens?.refreshToken || data.refreshToken;

      if (data.success && newAccessToken) {
        await this.setAuthToken(newAccessToken);
        if (newRefreshToken) {
          await this.setRefreshToken(newRefreshToken);
        }
        return true;
      } else {
        console.error('Invalid refresh response:', data);
        await this.clearAuthToken();
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      await this.clearAuthToken();
      return false;
    }
  }

  async isLoggedIn(): Promise<boolean> {
    const token = await this.getAuthToken();
    return !!token;
  }

  async getAuthHeaders(): Promise<Record<string, string>> {
    // Check if token is expired and refresh if needed
    const isExpired = await this.isTokenExpired();
    if (isExpired) {
      console.log('⏰ Token expired, attempting refresh...');
      const refreshed = await this.refreshAccessToken();
      if (!refreshed) {
        console.warn('⚠️ Token refresh failed, user needs to log in again');
        return {};
      }
    }

    const token = await this.getAuthToken();
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
      };
    }
    return {};
  }

  async handleAuthError(response: Response): Promise<boolean> {
    // If we get 401, try to refresh the token
    if (response.status === 401) {
      console.warn('⚠️ Got 401 Unauthorized, attempting token refresh...');
      const refreshed = await this.refreshAccessToken();

      if (refreshed) {
        console.log('✅ Token refreshed successfully after 401');
        return true; // Indicate that the request should be retried
      } else {
        console.error('❌ Token refresh failed, user needs to log in again');
        // Clear auth state and redirect to login would happen in the UI layer
        return false;
      }
    }
    return false;
  }

  async loginWithGoogle(
    googleToken: string,
    _userInfo: any,
  ): Promise<{ success: boolean; user?: any; token?: string; message: string }> {
    try {
      // ✅ BACKEND V3: Use /auth/google endpoint on Cloudflare Workers
      const response = await fetch(`${this.authURL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idToken: googleToken,
        }),
      });

      const data = await response.json();

      // Check for email provider conflict (409)
      if (response.status === 409 && data.error === 'EMAIL_PROVIDER_CONFLICT') {
        return {
          success: false,
          message:
            data.message ||
            `This email is already registered with ${data.registeredProvider}. Please use that sign-in method instead.`,
        };
      }

      if (data.success || response.ok) {
        const accessToken = data.tokens?.accessToken || data.token;
        const refreshToken = data.tokens?.refreshToken || data.refreshToken;

        if (accessToken) {
          await this.setAuthToken(accessToken);
        }
        if (refreshToken) {
          await this.setRefreshToken(refreshToken);
        }
        if (data.user) {
          localStorage.setItem('user_profile', JSON.stringify(data.user));
          localStorage.setItem('isLoggedIn', 'true');
        }

        return {
          success: true,
          user: data.user,
          token: accessToken,
          message: 'Successfully logged in with Google',
        };
      } else {
        return {
          success: false,
          message: data.message || 'Google login failed',
        };
      }
    } catch (error) {
      console.error('Google login error:', error);
      return {
        success: false,
        message: 'Failed to connect to authentication server',
      };
    }
  }

  async loginWithApple(
    authorizationCode: string,
    identityToken: string,
    userInfo?: any,
  ): Promise<{ success: boolean; user?: any; token?: string; message: string }> {
    try {
      console.log('🍎 Logging in with Apple...');

      // ✅ BACKEND V3: Use /auth/apple endpoint on Cloudflare Workers
      const response = await fetch(`${this.authURL}/auth/apple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identityToken,
          authorizationCode,
          name: userInfo?.name,
        }),
      });

      const data = await response.json();

      // Check for email provider conflict (409)
      if (response.status === 409 && data.error === 'EMAIL_PROVIDER_CONFLICT') {
        return {
          success: false,
          message:
            data.message ||
            `This email is already registered with ${data.registeredProvider}. Please use that sign-in method instead.`,
        };
      }

      if (data.success || response.ok) {
        const accessToken = data.tokens?.accessToken || data.token;
        const refreshToken = data.tokens?.refreshToken || data.refreshToken;

        if (accessToken) {
          await this.setAuthToken(accessToken);
        }
        if (refreshToken) {
          await this.setRefreshToken(refreshToken);
        }
        if (data.user) {
          localStorage.setItem('user_profile', JSON.stringify(data.user));
          localStorage.setItem('isLoggedIn', 'true');
        }

        return {
          success: true,
          user: data.user,
          token: accessToken,
          message: 'Successfully logged in with Apple',
        };
      } else {
        return {
          success: false,
          message: data.message || 'Apple login failed',
        };
      }
    } catch (error) {
      console.error('Apple login error:', error);
      return {
        success: false,
        message: 'Failed to connect to authentication server',
      };
    }
  }

  // ============= Email OTP Methods (via Resend) =============

  async sendEmailOTP(
    email: string,
    turnstileToken?: string,
  ): Promise<{ success: boolean; message: string; expiresIn?: number }> {
    try {
      const response = await fetch(`${this.authURL}/auth/email/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, turnstileToken }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          message: data.message || 'OTP sent to your email',
          expiresIn: data.expiresIn || 300,
        };
      } else {
        return {
          success: false,
          message: data.error || data.message || 'Failed to send OTP',
        };
      }
    } catch (error) {
      console.error('Send email OTP error:', error);
      return {
        success: false,
        message: 'Failed to connect to authentication server',
      };
    }
  }

  async verifyEmailOTP(
    email: string,
    code: string,
  ): Promise<{ success: boolean; message: string; token?: string; user?: any }> {
    try {
      const response = await fetch(`${this.authURL}/auth/email/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const accessToken = data.tokens?.accessToken || data.token;
        const refreshToken = data.tokens?.refreshToken || data.refreshToken;

        if (accessToken) {
          await this.setAuthToken(accessToken);
        }
        if (refreshToken) {
          await this.setRefreshToken(refreshToken);
        }
        if (data.user) {
          localStorage.setItem('user_profile', JSON.stringify(data.user));
          localStorage.setItem('isLoggedIn', 'true');
        }

        return {
          success: true,
          message: data.message || 'OTP verified successfully',
          token: accessToken,
          user: data.user,
        };
      } else {
        return {
          success: false,
          message: data.error || data.message || 'Invalid or expired OTP',
        };
      }
    } catch (error) {
      console.error('Verify email OTP error:', error);
      return {
        success: false,
        message: 'Failed to verify OTP',
      };
    }
  }

  // ============= Firebase Phone Auth Verification =============

  async verifyFirebasePhone(
    idToken: string,
  ): Promise<{ success: boolean; message: string; token?: string; user?: any }> {
    try {
      const response = await fetch(`${this.authURL}/auth/phone/verify-firebase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const accessToken = data.tokens?.accessToken || data.token;
        const refreshToken = data.tokens?.refreshToken || data.refreshToken;

        if (accessToken) {
          await this.setAuthToken(accessToken);
        }
        if (refreshToken) {
          await this.setRefreshToken(refreshToken);
        }
        if (data.user) {
          localStorage.setItem('user_profile', JSON.stringify(data.user));
          localStorage.setItem('isLoggedIn', 'true');
        }

        return {
          success: true,
          message: data.message || 'Phone verified successfully',
          token: accessToken,
          user: data.user,
        };
      } else {
        return {
          success: false,
          message: data.error || data.message || 'Phone verification failed',
        };
      }
    } catch (error) {
      console.error('Verify Firebase phone error:', error);
      return {
        success: false,
        message: 'Failed to verify phone number',
      };
    }
  }

  // ============= Profile Completion (New User Onboarding) =============

  async completeProfile(profileData: {
    displayName: string;
    username: string;
    dateOfBirth?: string;
    bio?: string;
  }): Promise<{ success: boolean; user?: any; token?: string; message: string }> {
    try {
      const response = await fetch(`${this.authURL}/auth/complete-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Backend returns new permanent tokens after onboarding
        const accessToken = data.tokens?.accessToken || data.token;
        const refreshToken = data.tokens?.refreshToken || data.refreshToken;

        if (accessToken) {
          await this.setAuthToken(accessToken);
        }
        if (refreshToken) {
          await this.setRefreshToken(refreshToken);
        }
        if (data.user) {
          localStorage.setItem('user_profile', JSON.stringify(data.user));
        }

        return {
          success: true,
          user: data.user,
          token: accessToken,
          message: data.message || 'Profile completed successfully',
        };
      } else {
        return {
          success: false,
          message: data.error || data.message || 'Profile completion failed',
        };
      }
    } catch (error) {
      console.error('Complete profile error:', error);
      return {
        success: false,
        message: 'Failed to complete profile',
      };
    }
  }

  async signUp(userData: {
    name: string;
    username: string;
    email: string;
    phone?: string;
    dateOfBirth: string;
    verificationCode: string;
  }): Promise<{ success: boolean; user?: any; token?: string; message: string }> {
    try {
      // ✅ BACKEND V2: Use Auth service for signup
      const response = await fetch(`${this.authURL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (response.ok && (data.success || data.success !== false)) {
        // Store auth data
        if (data.token) {
          await this.setAuthToken(data.token);
        }
        if (data.user) {
          localStorage.setItem('user_profile', JSON.stringify(data.user));
          localStorage.setItem('isLoggedIn', 'true');
        }

        return {
          success: true,
          user: data.user,
          token: data.token,
          message: data.message || 'Account created successfully',
        };
      } else {
        return {
          success: false,
          message: data.error || data.message || 'Sign up failed',
        };
      }
    } catch (error) {
      console.error('Sign up error:', error);
      return {
        success: false,
        message: 'Failed to create account',
      };
    }
  }

  async logout(): Promise<void> {
    await this.clearAuthToken();
  }

  // ============= Password Reset Methods =============

  async sendPasswordResetOTP(email: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log('📧 Sending password reset OTP to:', email);

      // ✅ BACKEND V2: Use Auth service for password reset
      const response = await fetch(`${this.authURL}/auth/password-reset/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          message: data.message || 'OTP sent to your email',
        };
      } else {
        return {
          success: false,
          message: data.message || data.error || 'Failed to send OTP',
        };
      }
    } catch (error) {
      console.error('Send password reset OTP error:', error);
      return {
        success: false,
        message: 'Failed to connect to server',
      };
    }
  }

  async verifyPasswordResetOTP(
    email: string,
    otp: string,
  ): Promise<{ success: boolean; message: string; resetToken?: string }> {
    try {
      console.log('🔐 Verifying password reset OTP...');

      // ✅ BACKEND V2: Use Auth service for OTP verification
      const response = await fetch(`${this.authURL}/auth/password-reset/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          otp,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          message: data.message || 'OTP verified',
          resetToken: data.resetToken,
        };
      } else {
        return {
          success: false,
          message: data.message || data.error || 'Invalid OTP',
        };
      }
    } catch (error) {
      console.error('Verify password reset OTP error:', error);
      return {
        success: false,
        message: 'Failed to verify OTP',
      };
    }
  }

  async resetPassword(
    email: string,
    otp: string,
    newPassword: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log('🔑 Resetting password...');

      // ✅ BACKEND V2: Use Auth service for password reset
      const response = await fetch(`${this.authURL}/auth/password-reset/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          otp,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          message: data.message || 'Password reset successfully',
        };
      } else {
        return {
          success: false,
          message: data.message || data.error || 'Failed to reset password',
        };
      }
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        message: 'Failed to reset password',
      };
    }
  }

  // ============= Public Post (no auth required) =============

  async getPublicPost(postId: string): Promise<{ success: boolean; post: any }> {
    try {
      const response = await fetch(`${this.postURL}/posts/${postId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        return { success: true, post: data.post || data };
      }
      return { success: false, post: null };
    } catch {
      return { success: false, post: null };
    }
  }

  async getPublicFeed(limit: number = 5): Promise<any[]> {
    try {
      const response = await fetch(`${this.postURL}/post/feed?limit=${limit}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        const body = data.body
          ? typeof data.body === 'string'
            ? JSON.parse(data.body)
            : data.body
          : data;
        return body.posts || [];
      }
      return [];
    } catch {
      return [];
    }
  }

  // ============= Feed Methods =============

  async getFeed(options: { limit?: number; page?: number } = {}): Promise<{
    success: boolean;
    posts: Post[];
    total: number;
    hasMore: boolean;
    source: string;
  }> {
    try {
      const params = new URLSearchParams();

      if (options.limit && options.limit <= 100) {
        params.append('limit', options.limit.toString());
      } else {
        params.append('limit', '20');
      }

      if (options.page) {
        params.append('page', options.page.toString());
      }

      // ✅ BACKEND V2: Use Post service for feed
      const url = params.toString()
        ? `${this.postURL}/post/feed?${params}`
        : `${this.postURL}/post/feed`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add auth headers if available
      const authHeaders = await this.getAuthHeaders();
      Object.assign(headers, authHeaders);

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        const responseText = await response.text();

        let data;
        try {
          data = JSON.parse(responseText);

          // Handle wrapped response format
          if (data.statusCode !== undefined && data.body) {
            try {
              const actualResponse = JSON.parse(data.body);
              data = actualResponse;
            } catch {
              // Use original response
            }
          }
        } catch {
          data = { success: true, message: responseText, posts: [] };
        }

        // Normalize response
        const rawPosts = data.posts || data.data?.posts || (Array.isArray(data) ? data : []);

        // Normalize post structure to match web app expectations
        const posts = rawPosts.map((post: any) => ({
          postId: post.postId || post.id,
          userId: post.userId,
          type: post.mediaType ? (post.mediaType.includes('video') ? 'video' : 'image') : 'text',
          content: post.content || '',
          mediaUri: post.mediaUrl,
          mediaType: post.mediaType,
          thumbnailUri: post.thumbnailUrl,
          location: post.location
            ? {
                city: post.location.city || null,
                state: post.location.state || null,
                country: post.location.country || null,
                address: post.location.address || post.location.formattedAddress || null,
                latitude: post.location.latitude || post.latitude || null,
                longitude: post.location.longitude || post.longitude || null,
                coordinates:
                  post.location.latitude && post.location.longitude
                    ? [post.location.latitude, post.location.longitude]
                    : [post.latitude, post.longitude],
              }
            : undefined,
          onRecordCode: post.onRecordCode || post.postCode,
          privacy: post.privacy || 'public',
          allowComments: post.allowComments !== false,
          createdAt: post.createdAt || new Date(post.timestamp).toISOString(),
          updatedAt: post.updatedAt || post.createdAt || new Date(post.timestamp).toISOString(),
          author: post.author || {
            id: post.userId,
            name: post.authorName || 'Unknown',
            username: post.authorUsername || 'unknown',
            avatar: post.authorAvatar,
            verified: post.author?.verified || false,
            isReporter: post.author?.isReporter || false,
          },
          stats: {
            likes: post.likes || 0,
            comments: post.comments || 0,
            reposts: post.reposts || 0,
            views: post.views || 0,
          },
          // Media capture time (for media age in shield modal)
          capturedAt: post.capturedAt || null,
          // Repost metadata (passed through from backend)
          isRepost: post.isRepost || false,
          originalPostId: post.originalPostId || null,
          reposterName: post.reposterName || null,
          reposterUsername: post.reposterUsername || null,
        }));

        return {
          success: data.success !== false,
          posts: posts,
          total: data.pagination?.totalScanned || data.total || posts.length,
          hasMore: data.pagination?.hasMore || data.hasMore || false,
          source: 'api',
        };
      } else {
        console.error('Feed API error:', response.status);
        return this.getFallbackFeed(options);
      }
    } catch (error) {
      console.error('Error fetching feed:', error);
      return this.getFallbackFeed(options);
    }
  }

  // ============= Media Library Methods =============

  async getPrivateMedia(): Promise<{ success: boolean; media: any[] }> {
    try {
      const response = await fetch(`${this.authURL}/media/private`, {
        headers: await this.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return { success: true, media: data.media || data.privateMedia || [] };
      }
      return { success: false, media: [] };
    } catch {
      return { success: false, media: [] };
    }
  }

  async getSharedMedia(): Promise<{ success: boolean; media: any[] }> {
    try {
      const response = await fetch(`${this.authURL}/media/shared`, {
        headers: await this.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return { success: true, media: data.media || data.sharedMedia || [] };
      }
      return { success: false, media: [] };
    } catch {
      return { success: false, media: [] };
    }
  }

  // ============= Post Creation Methods =============

  async uploadMedia(file: File): Promise<{ success: boolean; url?: string; message: string }> {
    try {
      console.log('📤 Requesting upload URL for:', file.name);

      const response = await fetch(`${this.postURL}/api/media/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          size: file.size,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.status}`);
      }

      const data = await response.json();
      const { uploadUrl, mediaUrl } = data;

      if (!uploadUrl || !mediaUrl) {
        throw new Error('Invalid response from upload endpoint');
      }

      console.log('📤 Uploading media...');
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Media upload failed');
      }

      console.log('✅ Upload successful:', mediaUrl);
      return {
        success: true,
        url: mediaUrl,
        message: 'Upload successful',
      };
    } catch (error) {
      console.error('❌ Media upload error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }

  async createPost(postData: {
    text: string;
    mediaFile?: File;
    mediaType?: string;
    location?: any;
  }): Promise<{ success: boolean; postId?: string; message: string }> {
    try {
      console.log('📝 Creating post...');

      // Upload media if present
      let mediaUrl: string | undefined;
      if (postData.mediaFile) {
        const uploadResult = await this.uploadMedia(postData.mediaFile);
        if (!uploadResult.success) {
          return {
            success: false,
            message: `Media upload failed: ${uploadResult.message}`,
          };
        }
        mediaUrl = uploadResult.url;
      }

      // Generate on record code
      const onRecordCode = this.generateOnRecordCode();

      // ✅ BACKEND V2: Use Post service for post creation
      const response = await fetch(`${this.postURL}/post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          content: postData.text,
          mediaUrl,
          mediaType: postData.mediaType,
          location: postData.location,
          onRecordCode,
          privacy: 'public',
          allowComments: true,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Post creation failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      console.log('✅ Post created successfully:', data.postId);
      return {
        success: true,
        postId: data.postId || data.post?.postId,
        message: 'Post created successfully',
      };
    } catch (error) {
      console.error('❌ Post creation error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create post',
      };
    }
  }

  // ============= Social Interaction Methods =============

  async likePost(postId: string): Promise<{ success: boolean; isLiked: boolean }> {
    try {
      console.log('❤️ Liking post:', postId);

      // Optimistic update - store locally first
      const likes = this.getLocalLikes();
      likes.add(postId);
      localStorage.setItem('liked_posts', JSON.stringify([...likes]));

      // Get generic location (city only) for privacy-safe tracking
      const likeCity = await this.getGenericLocation();

      const response = await fetch(`${this.postURL}/api/posts/${postId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          likeCity,
        }),
      });

      if (!response.ok) {
        // Revert local change on failure
        likes.delete(postId);
        localStorage.setItem('liked_posts', JSON.stringify([...likes]));
        throw new Error(`Failed to like post: ${response.status}`);
      }

      console.log('✅ Post liked');
      return { success: true, isLiked: true };
    } catch (error) {
      console.error('Error liking post:', error);
      return { success: false, isLiked: false };
    }
  }

  async unlikePost(postId: string): Promise<{ success: boolean; isLiked: boolean }> {
    try {
      console.log('💔 Unliking post:', postId);

      // Optimistic update - store locally first
      const likes = this.getLocalLikes();
      likes.delete(postId);
      localStorage.setItem('liked_posts', JSON.stringify([...likes]));

      const response = await fetch(`${this.postURL}/api/posts/${postId}/like`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        // Revert local change on failure
        likes.add(postId);
        localStorage.setItem('liked_posts', JSON.stringify([...likes]));
        throw new Error(`Failed to unlike post: ${response.status}`);
      }

      console.log('✅ Post unliked');
      return { success: true, isLiked: false };
    } catch (error) {
      console.error('Error unliking post:', error);
      return { success: false, isLiked: true };
    }
  }

  isPostLiked(postId: string): boolean {
    const likes = this.getLocalLikes();
    return likes.has(postId);
  }

  isPostReposted(postId: string): boolean {
    const reposts = this.getLocalReposts();
    return reposts.has(postId);
  }

  private getLocalReposts(): Set<string> {
    try {
      const stored = localStorage.getItem('reposted_posts');
      if (stored) return new Set(JSON.parse(stored));
    } catch (_e) {
      /* non-critical */
    }
    return new Set();
  }

  async loadMyInteractions(): Promise<{ likes: string[]; saves: string[]; reposts: string[] }> {
    try {
      const response = await fetch(`${this.postURL}/api/posts/my-interactions`, {
        headers: { ...(await this.getAuthHeaders()) },
      });
      if (!response.ok) return { likes: [], saves: [], reposts: [] };
      const data = await response.json();
      if (data.success) {
        // Sync local caches
        localStorage.setItem('liked_posts', JSON.stringify(data.likes || []));
        localStorage.setItem('bookmarked_posts', JSON.stringify(data.saves || []));
        localStorage.setItem('reposted_posts', JSON.stringify(data.reposts || []));
      }
      return { likes: data.likes || [], saves: data.saves || [], reposts: data.reposts || [] };
    } catch (e) {
      return { likes: [], saves: [], reposts: [] };
    }
  }

  private getLocalLikes(): Set<string> {
    try {
      const stored = localStorage.getItem('liked_posts');
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading likes:', error);
    }
    return new Set();
  }

  // ============= Comment Methods =============

  async addComment(postId: string, content: string): Promise<{ success: boolean; comment?: any }> {
    try {
      console.log('💬 Adding comment to post:', postId);

      const response = await fetch(`${this.postURL}/api/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          postId,
          content,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to add comment: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Comment added');

      return {
        success: true,
        comment: data.comment || {
          id: data.commentId,
          postId,
          content,
          createdAt: new Date().toISOString(),
        },
      };
    } catch (error) {
      console.error('Error adding comment:', error);
      return { success: false };
    }
  }

  async getComments(postId: string): Promise<{ success: boolean; comments: any[] }> {
    try {
      console.log('💬 Fetching comments for post:', postId);

      const response = await fetch(`${this.postURL}/api/comments/${postId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch comments: ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Loaded ${data.comments?.length || 0} comments`);

      return {
        success: true,
        comments: data.comments || [],
      };
    } catch (error) {
      console.error('Error fetching comments:', error);
      return { success: true, comments: [] };
    }
  }

  // ============= Repost Methods =============

  async createRepost(data: {
    originalPostId: string;
    repostType: 'full' | 'media-only';
    repostText?: string;
  }): Promise<{ success: boolean; repost?: any }> {
    try {
      console.log('🔄 Creating repost:', data.originalPostId);

      const response = await fetch(`${this.postURL}/api/posts/${data.originalPostId}/repost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          repostType: data.repostType,
          repostText: data.repostText || '',
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create repost: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Repost created');

      return {
        success: true,
        repost: result.repost,
      };
    } catch (error) {
      console.error('Error creating repost:', error);
      return { success: false };
    }
  }

  async repostPost(postId: string): Promise<{ success: boolean; repostId?: string }> {
    // Wrapper for createRepost for backward compatibility
    const result = await this.createRepost({
      originalPostId: postId,
      repostType: 'full',
    });

    return {
      success: result.success,
      repostId: result.repost?.repostId,
    };
  }

  async createQuoteRepost(data: {
    originalPostId: string;
    text: string;
  }): Promise<{ success: boolean }> {
    try {
      // Create a new post with quoted_post_id reference
      const response = await fetch(`${this.postURL}/post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          content: data.text,
          quoted_post_id: data.originalPostId,
          type: 'quote',
        }),
      });

      if (!response.ok) {
        throw new Error(`Quote repost failed: ${response.status}`);
      }

      // Also record as repost interaction for the count
      try {
        await fetch(`${this.postURL}/api/posts/${data.originalPostId}/repost`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await this.getAuthHeaders()),
          },
          body: JSON.stringify({ repostType: 'full' }),
        });
      } catch (e) {
        /* count increment is best-effort */
      }

      return { success: true };
    } catch (error) {
      console.error('Quote repost error:', error);
      return { success: false };
    }
  }

  async deleteRepost(repostId: string): Promise<{ success: boolean }> {
    try {
      console.log('🗑️ Deleting repost:', repostId);

      const response = await fetch(`${this.postURL}/api/posts/${repostId}/repost`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete repost: ${response.status}`);
      }

      console.log('✅ Repost deleted');
      return { success: true };
    } catch (error) {
      console.error('Error deleting repost:', error);
      return { success: false };
    }
  }

  async unrepost(postId: string): Promise<{ success: boolean }> {
    // Backward compatibility wrapper
    return await this.deleteRepost(postId);
  }

  async getRepostStatus(
    postId: string,
  ): Promise<{ success: boolean; hasReposted: boolean; repostId?: string }> {
    try {
      const response = await fetch(`${this.postURL}/api/posts/${postId}/repost-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get repost status: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: true,
        hasReposted: data.hasReposted || false,
        repostId: data.repostId,
      };
    } catch (error) {
      console.error('Error getting repost status:', error);
      return { success: false, hasReposted: false };
    }
  }

  // ============= Share Tracking =============

  async trackShare(
    postId: string,
    shareData: { platform: string; shareType: string; metadata?: any },
  ): Promise<{ success: boolean }> {
    try {
      console.log('📤 Tracking share:', postId, shareData.platform);

      const response = await fetch(`${this.postURL}/api/posts/${postId}/view`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          platform: shareData.platform,
          shareType: shareData.shareType,
          metadata: shareData.metadata,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to track share: ${response.status}`);
      }

      console.log('✅ Share tracked');
      return { success: true };
    } catch (error) {
      console.error('Error tracking share:', error);
      // Don't fail share action if tracking fails
      return { success: false };
    }
  }

  // ============= User Profile Methods =============

  async getUserProfile(userId?: string): Promise<any> {
    try {
      if (!userId) {
        // Get current user from localStorage
        const stored = localStorage.getItem('user_profile');
        if (stored) {
          return JSON.parse(stored);
        }
      }

      console.log('👤 Fetching user profile:', userId || 'current user');

      // ✅ BACKEND V2: Use User service for profile
      const endpoint = userId ? `/user/profile?userId=${userId}` : '/user/profile';
      const response = await fetch(`${this.userURL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.status}`);
      }

      const data = await response.json();
      const profile = data.profile || data.user || data;

      // Cache current user profile
      if (!userId) {
        localStorage.setItem('user_profile', JSON.stringify(profile));
      }

      console.log('✅ Profile loaded');
      return profile;
    } catch (error) {
      console.error('Error fetching user profile:', error);

      // Return cached data for current user
      if (!userId) {
        const stored = localStorage.getItem('user_profile');
        if (stored) return JSON.parse(stored);
      }

      return null;
    }
  }

  async updateUserProfile(
    profileData: any,
  ): Promise<{ success: boolean; profile?: any; message?: string }> {
    try {
      console.log('✏️ Updating user profile...');
      console.log('📤 Profile data:', profileData);

      // ✅ BACKEND V2: Use User service for profile update
      const response = await fetch(`${this.userURL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          ...profileData,
          timestamp: new Date().toISOString(),
        }),
      });

      const data = await response.json();
      console.log('📥 Profile update response:', {
        status: response.status,
        success: data.success,
        hasProfile: !!data.profile,
        message: data.message,
        error: data.error,
      });

      if (response.status === 401) {
        console.error('❌ Session expired');
        return {
          success: false,
          message: 'Session expired. Please log in again.',
        };
      }

      if (!response.ok) {
        console.error('❌ Profile update failed:', response.status, data);
        return {
          success: false,
          message: data.error || data.message || `Server error: ${response.status}`,
        };
      }

      if (data.success || response.ok) {
        const updatedProfile = data.profile || data.user || data;

        // Update local cache
        localStorage.setItem('user_profile', JSON.stringify(updatedProfile));
        console.log('✅ Profile updated');

        return { success: true, profile: updatedProfile };
      } else {
        return {
          success: false,
          message: data.error || data.message || 'Failed to update profile',
        };
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update profile',
      };
    }
  }

  async checkUsernameAvailability(
    username: string,
  ): Promise<{ available: boolean; valid: boolean; message?: string; errorCode?: string }> {
    try {
      console.log('🔍 Checking username availability:', username);

      const response = await fetch(
        `${this.authURL}/auth/check-username?username=${encodeURIComponent(username)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        return {
          available: data.available === true,
          valid: data.valid !== false,
          message: data.message,
          errorCode: data.errorCode,
        };
      }

      const errorData = await response.json().catch(() => null);
      return {
        available: false,
        valid: false,
        message: errorData?.message || 'Unable to verify username',
      };
    } catch (error) {
      console.error('Username check error:', error);
      return {
        available: false,
        valid: false,
        message: 'Unable to connect to server. Please try again.',
      };
    }
  }

  async uploadProfileImage(
    imageFile: File,
  ): Promise<{ success: boolean; url?: string; message: string }> {
    try {
      console.log('📸 Uploading profile image...');

      // Convert image file to base64
      const base64Image = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });

      console.log('📤 Uploading to avatar endpoint...');

      // ✅ BACKEND V2: Use User service avatar upload endpoint
      const response = await fetch(`${this.userURL}/user/avatar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          imageBase64: base64Image,
          contentType: imageFile.type,
        }),
      });

      const data = await response.json();

      console.log('📥 Avatar upload response:', {
        status: response.status,
        success: data.success,
        hasUrl: !!data.avatarUrl,
        message: data.message,
      });

      if (response.status === 401) {
        return {
          success: false,
          message: 'Session expired. Please log in again.',
        };
      }

      if (!response.ok || !data.success) {
        return {
          success: false,
          message: data.message || `Upload failed: ${response.status}`,
        };
      }

      console.log('✅ Profile image updated');
      return {
        success: true,
        url: data.avatarUrl || data.url,
        message: data.message || 'Profile image updated successfully',
      };
    } catch (error) {
      console.error('Error uploading profile image:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to upload profile image',
      };
    }
  }

  async getUserPosts(
    userId: string,
    options: { limit?: number; page?: number } = {},
  ): Promise<{
    success: boolean;
    posts: Post[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      console.log('📄 Fetching user posts:', userId);

      const params = new URLSearchParams();
      params.append('userId', userId);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.page) params.append('page', options.page.toString());

      // ✅ BACKEND V2: Use unified API user posts endpoint
      const url = `${this.postURL}/api/users/${userId}/posts?${params}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user posts: ${response.status}`);
      }

      const data = await response.json();
      const posts = data.posts || [];

      console.log(`✅ Loaded ${posts.length} posts`);

      return {
        success: true,
        posts,
        total: data.total || posts.length,
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error fetching user posts:', error);
      return {
        success: false,
        posts: [],
        total: 0,
        hasMore: false,
      };
    }
  }

  async followUser(userId: string): Promise<{ success: boolean }> {
    try {
      console.log('➕ Following user:', userId);

      // ✅ BACKEND V2: Use User service for follow
      const response = await fetch(`${this.userURL}/user/follow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          targetUserId: userId,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to follow user: ${response.status}`);
      }

      console.log('✅ User followed');
      return { success: true };
    } catch (error) {
      console.error('Error following user:', error);
      return { success: false };
    }
  }

  async unfollowUser(userId: string): Promise<{ success: boolean }> {
    try {
      console.log('➖ Unfollowing user:', userId);

      // ✅ BACKEND V2: Use User service for unfollow
      const response = await fetch(`${this.userURL}/user/follow`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          targetUserId: userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to unfollow user: ${response.status}`);
      }

      console.log('✅ User unfollowed');
      return { success: true };
    } catch (error) {
      console.error('Error unfollowing user:', error);
      return { success: false };
    }
  }

  async getFollowers(
    userId: string,
    options: { limit?: number; page?: number } = {},
  ): Promise<{
    success: boolean;
    users: any[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      console.log('👥 Fetching followers:', userId);

      const params = new URLSearchParams();
      params.append('userId', userId);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.page) params.append('page', options.page.toString());

      // ✅ BACKEND V2: Use User service for followers
      const url = `${this.userURL}/user/followers?${params}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch followers: ${response.status}`);
      }

      const data = await response.json();
      const users = data.followers || data.users || [];

      console.log(`✅ Loaded ${users.length} followers`);

      return {
        success: true,
        users,
        total: data.total || users.length,
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error fetching followers:', error);
      return {
        success: false,
        users: [],
        total: 0,
        hasMore: false,
      };
    }
  }

  async getFollowing(
    userId: string,
    options: { limit?: number; page?: number } = {},
  ): Promise<{
    success: boolean;
    users: any[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      console.log('👤 Fetching following:', userId);

      const params = new URLSearchParams();
      params.append('userId', userId);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.page) params.append('page', options.page.toString());

      // ✅ BACKEND V2: Use User service for following
      const url = `${this.userURL}/user/following?${params}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch following: ${response.status}`);
      }

      const data = await response.json();
      const users = data.following || data.users || [];

      console.log(`✅ Loaded ${users.length} following`);

      return {
        success: true,
        users,
        total: data.total || users.length,
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error fetching following:', error);
      return {
        success: false,
        users: [],
        total: 0,
        hasMore: false,
      };
    }
  }

  async getUserSuggestions(): Promise<{ success: boolean; suggestions: any[] }> {
    try {
      const response = await fetch(`${this.postURL}/api/users/suggestions`, {
        headers: await this.getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        return { success: true, suggestions: data.suggestions || [] };
      }
      return { success: true, suggestions: [] };
    } catch {
      return { success: true, suggestions: [] };
    }
  }

  async searchLocations(_options: {
    query: string;
    limit?: number;
  }): Promise<{ success: boolean; locations: any[] }> {
    // Location search — returns empty until backend endpoint exists
    return { success: true, locations: [] };
  }

  async searchUsers(
    query: string,
    options: { limit?: number; page?: number } = {},
  ): Promise<{
    success: boolean;
    users: any[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      console.log('🔍 Searching users:', query);

      const params = new URLSearchParams();
      params.append('q', query);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.page) params.append('page', options.page.toString());

      const response = await fetch(`${this.postURL}/user/search?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to search users: ${response.status}`);
      }

      const data = await response.json();
      const users = data.users || data.results || [];

      console.log(`✅ Found ${users.length} users`);

      return {
        success: true,
        users,
        total: data.total || users.length,
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error searching users:', error);
      return {
        success: false,
        users: [],
        total: 0,
        hasMore: false,
      };
    }
  }

  // ============= Bookmark Methods =============

  async bookmarkPost(postId: string): Promise<{ success: boolean }> {
    const bookmarks = this.getLocalBookmarks();
    bookmarks.add(postId);
    localStorage.setItem('bookmarked_posts', JSON.stringify([...bookmarks]));

    try {
      await fetch(`${this.postURL}/api/posts/${postId}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) },
      });
    } catch (e) {
      // Local save persists even if backend fails
    }
    return { success: true };
  }

  async unbookmarkPost(postId: string): Promise<{ success: boolean }> {
    const bookmarks = this.getLocalBookmarks();
    bookmarks.delete(postId);
    localStorage.setItem('bookmarked_posts', JSON.stringify([...bookmarks]));

    try {
      await fetch(`${this.postURL}/api/posts/${postId}/save`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) },
      });
    } catch (e) {
      // Local unsave persists even if backend fails
    }
    return { success: true };
  }

  isPostBookmarked(postId: string): boolean {
    const bookmarks = this.getLocalBookmarks();
    return bookmarks.has(postId);
  }

  private getLocalBookmarks(): Set<string> {
    try {
      const stored = localStorage.getItem('bookmarked_posts');
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
    return new Set();
  }

  async getBookmarkedPosts(options: { limit?: number; page?: number } = {}): Promise<{
    success: boolean;
    posts: Post[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      console.log('🔖 Fetching bookmarked posts...');

      const params = new URLSearchParams();
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.page) params.append('page', options.page.toString());

      const url = params.toString()
        ? `${this.postURL}/api/bookmarks?${params}`
        : `${this.postURL}/api/bookmarks`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (response.status === 401) {
        // Try refreshing token and retry once
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          const retryResponse = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(await this.getAuthHeaders()),
            },
          });
          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            return {
              success: true,
              posts: retryData.posts || [],
              total: retryData.total || 0,
              hasMore: retryData.hasMore || false,
            };
          }
        }
        throw new Error('Please log in to view bookmarks');
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch bookmarks: ${response.status}`);
      }

      const data = await response.json();
      const posts = data.posts || data.bookmarks || [];

      console.log(`✅ Loaded ${posts.length} bookmarked posts`);

      return {
        success: true,
        posts,
        total: data.total || posts.length,
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      // Return empty for now - backend needs to implement this endpoint
      return {
        success: false,
        posts: [],
        total: 0,
        hasMore: false,
      };
    }
  }

  // ============= Explore Methods =============

  async getExploreFeed(
    options: {
      type?: 'trending' | 'latest';
      category?: string;
      limit?: number;
      page?: number;
    } = {},
  ): Promise<{
    success: boolean;
    posts: Post[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      console.log('🔍 Fetching explore feed...');

      const params = new URLSearchParams();
      if (options.type) params.append('type', options.type);
      if (options.category) params.append('category', options.category);
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.page) params.append('page', options.page.toString());

      const url = params.toString()
        ? `${this.postURL}/api/discover/feed?${params}`
        : `${this.postURL}/api/discover/feed`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch explore feed: ${response.status}`);
      }

      const data = await response.json();
      const posts = data.posts || [];

      console.log(`✅ Loaded ${posts.length} explore posts`);

      return {
        success: true,
        posts,
        total: data.total || posts.length,
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error fetching explore feed:', error);
      // Fallback to regular feed
      return await this.getFeed(options);
    }
  }

  async getTrendingHashtags(options: { limit?: number } = {}): Promise<{
    success: boolean;
    hashtags: Array<{ tag: string; count: number }>;
  }> {
    try {
      console.log('📈 Fetching trending hashtags...');

      const params = new URLSearchParams();
      if (options.limit) params.append('limit', options.limit.toString());

      const url = params.toString()
        ? `${this.postURL}/api/discover/trending/hashtags?${params}`
        : `${this.postURL}/api/discover/trending/hashtags`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch trending hashtags: ${response.status}`);
      }

      const data = await response.json();
      const hashtags = data.hashtags || data.trending || [];

      console.log(`✅ Loaded ${hashtags.length} trending hashtags`);

      return {
        success: true,
        hashtags,
      };
    } catch (error) {
      console.error('Error fetching trending hashtags:', error);
      // Return mock data as fallback
      return {
        success: true,
        hashtags: [
          { tag: 'breaking', count: 12500 },
          { tag: 'news', count: 8900 },
          { tag: 'onrecord', count: 7200 },
          { tag: 'journalism', count: 5600 },
          { tag: 'trending', count: 4300 },
        ],
      };
    }
  }

  async searchPosts(options: { query: string; limit?: number; page?: number }): Promise<{
    success: boolean;
    posts: Post[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      console.log('🔍 Searching posts:', options.query);

      const params = new URLSearchParams();
      params.append('code', options.query); // Backend V2 uses 'code' for on record code search
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.page) params.append('page', options.page.toString());

      // ✅ BACKEND V2: Use Post service for search
      const response = await fetch(`${this.postURL}/post/search?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to search posts: ${response.status}`);
      }

      const data = await response.json();
      const posts = data.posts || data.results || [];

      console.log(`✅ Found ${posts.length} posts`);

      return {
        success: true,
        posts,
        total: data.total || posts.length,
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error searching posts:', error);
      return {
        success: false,
        posts: [],
        total: 0,
        hasMore: false,
      };
    }
  }

  // ============= Location Services (Privacy-Safe) =============

  /**
   * Get generic location (city only) for privacy-safe tracking
   * No user identification, only city-level data
   */
  async getGenericLocation(): Promise<string | null> {
    try {
      // Check cache first (1 hour expiry)
      const cached = localStorage.getItem('generic_location');
      const cacheTime = localStorage.getItem('generic_location_time');

      if (cached && cacheTime) {
        const age = Date.now() - parseInt(cacheTime);
        if (age < 3600000) {
          // 1 hour
          return cached;
        }
      }

      // Get approximate location from browser (no exact coordinates stored)
      if ('geolocation' in navigator) {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false, // Less precise = more privacy
            timeout: 5000,
            maximumAge: 3600000, // Use cached location if available
          });
        });

        // Use approximate coordinates to get city name
        const city = await this.geocodeToCity(position.coords.latitude, position.coords.longitude);

        if (city) {
          // Cache the city name (no coordinates)
          localStorage.setItem('generic_location', city);
          localStorage.setItem('generic_location_time', Date.now().toString());
          return city;
        }
      }

      return null;
    } catch (error) {
      console.log('Location not available:', error);
      return null;
    }
  }

  /**
   * Reverse geocode coordinates to city name using OpenStreetMap
   * Privacy-friendly alternative to Google Maps
   */
  private async geocodeToCity(lat: number, lon: number): Promise<string | null> {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10`,
        {
          headers: {
            'User-Agent': 'OnRecord-Web/1.0',
          },
        },
      );

      if (!response.ok) return null;

      const data = await response.json();

      // Extract city name
      const city =
        data.address?.city ||
        data.address?.town ||
        data.address?.village ||
        data.address?.municipality ||
        data.address?.county;

      return city ? city.toLowerCase().trim() : null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }

  /**
   * Get aggregated city data for a post (privacy-safe analytics)
   * No user data, just city counts
   */
  async getLikeCities(postId: string): Promise<{
    success: boolean;
    cities: Array<{ city: string; likesCount: number }>;
    totalCities: number;
  }> {
    try {
      console.log('🌍 Fetching like cities for post:', postId);

      const response = await fetch(`${this.postURL}/api/posts/${postId}/analytics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch like cities: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: true,
        cities: data.cities || [],
        totalCities: data.totalCities || 0,
      };
    } catch (error) {
      console.error('Error fetching like cities:', error);
      return {
        success: false,
        cities: [],
        totalCities: 0,
      };
    }
  }

  // ============= on record Code Generation =============

  generateOnRecordCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  // ============= Fallback Data =============

  private getFallbackFeed(options: any): {
    success: boolean;
    posts: Post[];
    total: number;
    hasMore: boolean;
    source: string;
  } {
    const demoPosts = this.createDemoPostsData();

    let filteredPosts = demoPosts;
    if (options.limit) {
      filteredPosts = filteredPosts.slice(0, options.limit);
    }

    return {
      success: true,
      posts: filteredPosts,
      total: filteredPosts.length,
      hasMore: demoPosts.length > filteredPosts.length,
      source: 'demo',
    };
  }

  private createDemoPostsData(): Post[] {
    const currentTime = new Date().toISOString();
    const baseMediaUrl = 'https://media.onrecord.live/uploaded';

    return [
      {
        postId: 'post_demo_001',
        userId: 'user_demo_reporter_123',
        type: 'text' as const,
        content:
          'Breaking: Local community center receives major renovation funding! 🏢✨ This will transform how we serve our neighborhood.',
        location: {
          city: 'Mumbai',
          country: 'India',
          coordinates: [19.076, 72.8777],
        },
        onRecordCode: 'ONR12AB34CD5',
        privacy: 'public',
        allowComments: true,
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        updatedAt: currentTime,
        author: {
          id: 'user_demo_reporter_123',
          name: 'Mumbai Reporter',
          username: 'mumbai_reporter',
          verified: true,
          isReporter: true,
        },
        stats: {
          likes: 47,
          comments: 12,
          reposts: 8,
          views: 234,
        },
      },
      {
        postId: 'post_demo_002',
        userId: 'user_demo_citizen_456',
        type: 'image' as const,
        content: 'Beautiful sunset from Marine Drive today! Perfect evening for a walk 🌅',
        mediaUri: `${baseMediaUrl}/demo/sunset_marine_drive.jpg`,
        mediaType: 'image',
        thumbnailUri: `${baseMediaUrl}/demo/thumbnails/sunset_marine_drive_thumb.jpg`,
        onRecordCode: 'ONR98XY76ZW3',
        location: {
          city: 'Mumbai',
          country: 'India',
          coordinates: [18.9432, 72.8236],
        },
        privacy: 'public',
        allowComments: true,
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        updatedAt: currentTime,
        author: {
          id: 'user_demo_citizen_456',
          name: 'Priya Sharma',
          username: 'priya_mumbai',
          avatar: `${baseMediaUrl}/avatars/priya_avatar.jpg`,
          verified: false,
          isReporter: false,
        },
        stats: {
          likes: 89,
          comments: 23,
          reposts: 15,
          views: 445,
        },
      },
      {
        postId: 'post_demo_003',
        userId: 'user_demo_reporter_789',
        type: 'video' as const,
        content:
          'LIVE: New Metro line testing begins! First trains running on the extended route 🚇',
        mediaUri: `${baseMediaUrl}/demo/metro_testing.mp4`,
        mediaType: 'video',
        thumbnailUri: `${baseMediaUrl}/demo/thumbnails/metro_testing_thumb.jpg`,
        onRecordCode: 'ONR45QR89ST1',
        location: {
          city: 'Mumbai',
          country: 'India',
          coordinates: [19.0825, 72.8754],
        },
        privacy: 'public',
        allowComments: true,
        createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
        updatedAt: currentTime,
        author: {
          id: 'user_demo_reporter_789',
          name: 'Metro News Mumbai',
          username: 'metro_news_mumbai',
          avatar: `${baseMediaUrl}/avatars/metro_news_avatar.jpg`,
          verified: true,
          isReporter: true,
        },
        stats: {
          likes: 156,
          comments: 34,
          reposts: 28,
          views: 789,
        },
      },
      {
        postId: 'post_demo_004',
        userId: 'user_demo_citizen_101',
        type: 'text' as const,
        content:
          'Anyone else notice the new street lights on SV Road? Much brighter and safer for evening walks! 💡 Great job by the municipal corporation.',
        location: {
          city: 'Mumbai',
          country: 'India',
          coordinates: [19.0543, 72.832],
        },
        privacy: 'public',
        allowComments: true,
        createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
        updatedAt: currentTime,
        author: {
          id: 'user_demo_citizen_101',
          name: 'Rahul Gupta',
          username: 'rahul_bandra',
          verified: false,
          isReporter: false,
        },
        stats: {
          likes: 28,
          comments: 7,
          reposts: 3,
          views: 134,
        },
      },
    ];
  }
}

// Export singleton instance
export const api = new OnRecordWebAPI();
export default api;
